// services/apiService.js
import axios from "axios";

// Base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Generalized API request function
const apiRequest = async (method, endpoint, data = null) => {
  try {
    const config = {
      method,
      url: `${API_BASE_URL}/${endpoint}`,
      headers: { "Content-Type": "application/json" },
      data, // Only included for POST/PUT requests
      withCredentials: true, // This is crucial for sending/receiving cookies
    };
    const response = await axios(config);
    return response.data;
  } catch (error) {
    // Pass the whole error object to be handled in the calling function
    throw error;
  }
};

// --- Authentication ---
/**
 * Logs in a user with a mobile number and password.
 * @param {string} mobile - The user's mobile number.
 * @param {string} password - The user's password.
 * @returns {Promise<object>} The API response.
 */
export const loginUser = (mobile, password) =>
  apiRequest("post", "login", { mobile, password });

/**
 * Creates a new user account.
 * @param {object} userData - User data.
 * @param {string} userData.name - The user's name.
 * @param {string} userData.password - The user's password.
 * @param {string} [userData.email] - The user's email (optional).
 * @param {string} [userData.mobile] - The user's mobile (optional).
 * @returns {Promise<object>} The API response.
 */
export const createUser = (userData) =>
  apiRequest("post", "createuser", userData);

/**
 * Checks the status of a user based on their mobile number.
 * @param {string} mobile - The user's mobile number.
 * @returns {Promise<object>} The API response with user status.
 */
export const checkUserStatus = (mobile) =>
  apiRequest("post", "check-user-status", { mobile });

// --- College Data ---
export const loadApColleges = (formData) =>
  apiRequest("post", "College", {
    caste: formData.caste,
    gender: formData.gender,
    rank: formData.rank,
    year: formData.year, // Include year in the request
  });

export const loadApCollegesYear = (formData) =>
  apiRequest("post", "apcollegesyear", {
    caste: formData.caste,
    gender: formData.gender,
    rank: formData.rank,
    year: formData.year, // Include year in the request
  });

export const loadTsCollegesYear = (formData) =>
  apiRequest("post", "tscollegesyear", {
    caste: formData.caste,
    gender: formData.gender,
    rank: formData.rank,
    year: formData.year, // Include year in the request
  });

export const loadTsColleges = (formData) =>
  apiRequest("post", "tscolleges", {
    caste: formData.caste,
    gender: formData.gender,
    rank: formData.rank,
  });

export const loadTsPharmacyColleges = (formData) => {
  // console.log("Form Data:", formData);
  return apiRequest("post", "tspharmacyroute", {
    caste: formData.caste,
    gender: formData.gender,
    rank: formData.rank,
    year: formData.year,
  });
};

export const loadApPharmacyColleges = (formData) =>
  apiRequest("post", "appharmacyroute", {
    caste: formData.caste,
    gender: formData.gender,
    rank: formData.rank,
    year: formData.year,
  });

// --- Form Submission Data Logging ---
export const logCollegeSearchSubmission = async (formData) => {
  try {
    const response = await apiRequest("post", "submit-college-search", {
      examType: formData.examType,
      gender: formData.gender,
      caste: formData.caste,
      rank: formData.rank,
      location: formData.location,
      // 'year' is not sent as per backend route definition
    });
    // console.log("College search form data logged successfully.", response);
  } catch (error) {
    console.warn(
      "Failed to log college search submission (ignored):",
      error.message
    );
  }
};

export const logRankPredictorSubmission = async (formData) => {
  try {
    // The backend route is '/submit-rank-predictor'
    const response = await apiRequest("post", "submit-rank-predictor", {
      stream: formData.stream,
      firstYearMarks: formData.firstYearMarks,
      secondYearMarks: formData.secondYearMarks,
      eapcetMarks: formData.eapcetMarks,
    });
    // console.log("Rank predictor form data logged successfully.", response);
  } catch (error) {
    console.warn(
      "Failed to log rank predictor submission (ignored):",
      error.message
    );
  }
};

// --- Favorites Management ---
export const saveSelectionAPI = (selectedColleges, formData, name) =>
  apiRequest("post", "addfav", {
    favorites: {
      name,
      colleges: selectedColleges,
      formData,
      savedAt: new Date().toISOString(),
    },
  });

export const saveFavoriteList = async (favoriteData) => {
  try {
    const response = await apiRequest("post", "addfav", {
      favorites: {
        name: favoriteData.name,
        colleges: favoriteData.colleges,
        formData: favoriteData.formData,
        savedAt: new Date().toISOString(),
        // No need to add shareCode here, the backend will generate it
      },
    });

    return response;
  } catch (error) {
    console.error("Error saving favorite list:", error);
    throw error; // Propagate error for handling in components
  }
};

export const updateFavoriteList = (index, favoriteData) =>
  apiRequest("post", "updatefav", {
    index: index, // Send the list's index
    favorites: {  // Nest the data inside a 'favorites' object
      ...favoriteData,
      savedAt: new Date().toISOString(),
    },
  });

export const fetchFavorites = async () => {
  const data = await apiRequest("post", "getfavorites");
  return data.favorites || [];
};

export const fetchUserFavorites = async () => {
  // Pass an empty object as the data parameter for the POST request
  const data = await apiRequest("post", "getfavorites", {});
  if (!data.success) throw new Error(data.error || "Failed to load favorites");
  return data.favorites || [];
};

export const deleteFavoriteList = async (index) => {
  const data = await apiRequest("post", "deletefav", { index });
  if (!data.success) throw new Error(data.error || "Failed to delete list");
  return data;
};

export const renameFavoriteList = async (index, newName) => {
  const data = await apiRequest("post", "renamefav", { index, newName });
  if (!data.success) throw new Error(data.error || "Failed to rename list");
  return data;
};

// Update the shareFavoriteList function
export const shareFavoriteList = async (index) => {
  const data = await apiRequest("post", "sharefav", { index });
  if (!data.success)
    throw new Error(data.error || "Failed to create share link");
  return data.shareCode;
};

export const shareFavoriteById = async (favoriteId) => {
  const data = await apiRequest("post", "sharefavwithfavid", { favoriteId });
  if (!data.success)
    throw new Error(data.error || "Failed to create share link");
  return data.shareCode;
};

// Add this new function to get a single favorite by index
export const getFavoriteById = (index) =>
  apiRequest("post", "getfavoritebyid", { index });

export const fetchSharedList = (userId, favoriteId) =>
  apiRequest("get", `getshared/${userId}/${favoriteId}`);

export const fetchUserDetails = async () => {
  try {
    // The backend route is now a GET request and doesn't need a body.
    const data = await apiRequest("get", "getuser");
    if (data.success) {
      return {
        name: data.user.name || "",
        mobile: data.user.mobile || "",
        email: data.user.email || "",
        state: data.user.state || "",
        district: data.user.district || "",
        eamcetRank: data.user.eamcetRank || "",
      };
    } else {
      throw new Error(data.error || "Failed to fetch user details");
    }
  } catch (error) {
    console.error("Error fetching user details:", error);
    // Propagate a more specific error message if available from the backend response
    throw new Error(error.response?.data?.error || "Failed to fetch user details");
  }
};

export const checkAuthStatusApi = async () => {
  return apiRequest("get", "checkauth");
};

export const logoutUserApi = () => apiRequest("post", "logout", {});

export const fetchAp2025Particolleges = async () => {
  try {
    const data = await apiRequest("get", "ap2025pclg");
    // console.log(data.data)
    if (!data.success) {
      throw new Error(data.error || "Failed to fetch AP EAMCET colleges");
    }
    return data.data; // Assuming the API returns colleges in a 'colleges' field
  } catch (error) {
    console.error("Error fetching AP EAMCET colleges:", error);
    throw error;
  }
};

export const updateUserDetails = (details) =>
  apiRequest("post", "updateuser", details);

// --- OTPless Authentication ---
export const sendSignupWhatsappOtp = (phone) =>
  apiRequest("post", "send-signup-whatsapp-otp", { phone });

export const sendSignupVoiceOtp = (phone) =>
  apiRequest("post", "send-signup-voice-otp", { phone });

// NEW: Functions for sending OTP for Login
export const sendLoginWhatsappOtp = (phone) =>
  apiRequest("post", "send-login-whatsapp-otp", { phone });

export const sendLoginVoiceOtp = (phone) =>
  apiRequest("post", "send-login-voice-otp", { phone });

export const verifyOtp = (phone, otp, requestId) =>
  apiRequest("post", "verify-otp", { phone, otp, requestId });

// NEW: Function specifically for sending OTP for password reset
export const sendPasswordResetOtpApi = (phone) =>
  apiRequest("post", "send-password-reset-otp", { phone });

// --- Password Reset ---
export const resetPasswordApi = (mobile, newPassword) =>
  apiRequest("post", "resetpassword", { mobile, newPassword });

// Contact form submission
export const submitContactForm = (formData) =>
  apiRequest("post", "contact", formData);

// NEW: Function for sending login OTP
export const sendLoginOtp = (mobile) =>
  apiRequest("post", "send-password-reset-otp", { phone: mobile });

// Add this new function for feedback submission
export const submitFeedback = (formData) =>
  apiRequest("post", "feedback", formData);

// --- New Branches 2024 ---
export const fetchNewBranches2024 = async () => {
  try {
    const response = await apiRequest("get", "new-branches-2024");
    if (!response.success) {
      throw new Error(response.message || "Failed to fetch new branches data");
    }
    return response;
  } catch (error) {
    console.error("Error fetching new branches 2024:", error);
    throw error;
  }
};

// --- AP 2024 Colleges ---
export const fetchAp2024Colleges = async () => {
  try {
    const response = await apiRequest("get", "ap-2024-colleges");
    if (!response.success) {
      throw new Error(
        response.message || "Failed to fetch AP 2024 colleges data"
      );
    }
    return response;
  } catch (error) {
    console.error("Error fetching AP 2024 colleges:", error);
    throw error;
  }
};

// --- AP 2024 Branches ---
export const fetchAp2024Branches = async () => {
  try {
    const response = await apiRequest("get", "ap-2024-branches");
    if (!response.success) {
      throw new Error(
        response.message || "Failed to fetch AP 2024 branches data"
      );
    }
    return response;
  } catch (error) {
    console.error("Error fetching AP 2024 branches:", error);
    throw error;
  }
};

// --- AP 2025 Colleges ---
export const fetchAp2025EnggColleges = async () => {
  try {
    const response = await apiRequest("get", "ap-2025-engg-colleges");
    if (!response.success) {
      throw new Error(
        response.message || "Failed to fetch AP 2025 engg colleges data"
      );
    }
    // Return the 'data' array from the response object
    return response.data;
  } catch (error) {
    console.error("Error fetching AP 2025 engg colleges:", error);
    throw error; // Re-throw the error to be handled by the calling component
  }
};

export const fetchAp2025PharmacyColleges = async () => {
  try {
    const response = await apiRequest("get", "ap-2025-pharmacy-colleges");
    if (!response.success) {
      throw new Error(
        response.message || "Failed to fetch AP 2025 pharmacy colleges data"
      );
    }
    // Return the 'data' array from the response object
    return response.data;
  } catch (error) {
    console.error("Error fetching AP 2025 pharmacy colleges:", error);
    throw error; // Re-throw the error to be handled by the calling component
  }
};
